import { useEffect, useState } from "react";
import { ExternalLink, LogOut, Search, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { APP_NAME } from "@/lib/app";
import { AppLogo } from "@/components/app-logo";
import { HELP_LABEL, HELP_URL, NAV_GROUPS, SETTINGS_ITEM } from "./nav";

export function Topbar({ onSignOut }: { onSignOut: () => void }) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);

  // ⌘K / Ctrl+K öffnet die Suche von überall.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function go(to: string) {
    setSearchOpen(false);
    navigate(to);
  }

  return (
    <header className="sticky top-0 z-30 flex h-(--header-height) shrink-0 items-center gap-2 bg-topbar px-2 text-topbar-foreground md:px-3">
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <SidebarTrigger className="text-topbar-foreground hover:bg-white/10 hover:text-white" />
        <span className="flex items-center gap-1.5 truncate font-semibold text-white">
          <AppLogo className="size-7" />
          <span className="max-sm:sr-only">{APP_NAME}</span>
        </span>
      </div>

      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        className="flex h-8 w-full max-w-[480px] min-w-0 flex-[2] items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-2.5 text-left text-topbar-foreground/80 transition-colors outline-none hover:bg-white/15 focus-visible:ring-3 focus-visible:ring-ring/60"
      >
        <Search className="size-4 shrink-0" aria-hidden />
        <span className="flex-1 truncate">Seite suchen</span>
        <kbd className="hidden rounded border border-white/20 px-1.5 font-sans text-xs sm:inline">
          ⌘K
        </kbd>
      </button>

      <div className="flex flex-1 justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-white/10"
              aria-label="Konto"
            >
              <Avatar className="size-7">
                <AvatarFallback className="bg-[#c9f2dc] text-[#0c5132]">
                  <User className="size-4" />
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48">
            <DropdownMenuItem asChild>
              <a href={HELP_URL} target="_blank" rel="noreferrer">
                <ExternalLink />
                {HELP_LABEL}
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onSignOut}>
              <LogOut />
              Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CommandDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        title="Suchen"
        description="Zu einer Seite springen"
      >
        <CommandInput placeholder="Seite suchen…" />
        <CommandList>
          <CommandEmpty>Nichts gefunden.</CommandEmpty>
          {[...NAV_GROUPS, { label: "System", items: [SETTINGS_ITEM] }].map(
            (group) => (
              <CommandGroup key={group.label} heading={group.label}>
                {group.items.map((item) => (
                  <CommandItem
                    key={item.to}
                    value={item.label}
                    onSelect={() => go(item.to)}
                  >
                    <item.icon />
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ),
          )}
        </CommandList>
      </CommandDialog>
    </header>
  );
}
